import { actionProps } from "@/lib/controlAnalytics";
import { FILL_MODE_COPY, FILL_MODES, type FillMode } from "@/lib/exampleFill";

/** The paid fill's mark, as the extension draws it: one large star and a small one. */
export function Sparkles({ size = 16 }: { size?: number }) {
    return (
        <svg
            width={size}
            height={size}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={1.8}
            strokeLinejoin="round"
            aria-hidden="true"
        >
            <path d="M10 3.5l1.9 5.1 5.1 1.9-5.1 1.9L10 17.5l-1.9-5.1L3 10.5l5.1-1.9z" />
            <path d="M18 14.5l.9 2.1 2.1.9-2.1.9-.9 2.1-.9-2.1-2.1-.9 2.1-.9z" />
        </svg>
    );
}

/**
 * The two ways the extension fills an application, as two pressed-or-not
 * cards: what each spends and what pressing it does. The one pressed is
 * the one the example below shows.
 */
export default function FillModes({
    mode,
    onMode,
}: {
    mode: FillMode;
    onMode: (next: FillMode) => void;
}) {
    return (
        <div
            role="group"
            aria-label="How to fill the application"
            className="grid grid-cols-2 gap-3 max-[700px]:grid-cols-1"
        >
            {FILL_MODES.map((entry) => {
                const copy = FILL_MODE_COPY[entry];
                const pressed = mode === entry;
                return (
                    <button
                        {...actionProps("preview_fill_mode_selected", {
                            source: "ResumePreview",
                            next_state: entry,
                        })}
                        key={entry}
                        type="button"
                        aria-pressed={pressed}
                        aria-label={`${copy.name}, ${copy.cost.toLowerCase()}`}
                        aria-describedby={`fill-mode-${entry}`}
                        onClick={() => onMode(entry)}
                        className={`press flex flex-col gap-2.5 rounded-2xl border bg-card px-[18px] py-4 text-left text-fg transition-[border-color,box-shadow,transform] ${
                            pressed
                                ? "border-green shadow-[0_0_0_3px_var(--color-tint-green)]"
                                : "border-line shadow-card hover:border-line-strong"
                        }`}
                    >
                        <span className="flex flex-wrap items-center justify-between gap-2">
                            <span className="flex items-center gap-2 text-[15px] font-semibold">
                                {entry === "tailor" && <Sparkles />}
                                {copy.name}
                            </span>
                            <span
                                className={`rounded-full px-2.5 py-[3px] text-[11.5px] font-semibold ${
                                    entry === "tailor"
                                        ? "bg-tint-green text-brand-ink"
                                        : "bg-card-raised text-fg-muted"
                                }`}
                            >
                                {copy.cost}
                            </span>
                        </span>
                        <ol
                            id={`fill-mode-${entry}`}
                            className="m-0 flex list-none flex-col gap-1.5 p-0 text-[13px] leading-snug text-fg-muted"
                        >
                            {copy.steps.map((step, index) => (
                                <li key={step} className="flex gap-2">
                                    <span
                                        className="mt-px flex size-[18px] shrink-0 items-center justify-center rounded-full bg-card-raised font-mono text-[11px] font-bold text-fg"
                                        aria-hidden="true"
                                    >
                                        {index + 1}
                                    </span>
                                    {step}
                                </li>
                            ))}
                        </ol>
                    </button>
                );
            })}
        </div>
    );
}
